/**
 * ModelPricePlansDialog — operator preview of the financing plans a model
 * offers (ADR-0014). One card per offerable tenor, each figure pulled from the
 * server's /quote endpoint so the preview is the SAME computation the dealer
 * app books with (the single source of truth) — no client-side schedule math
 * that could drift from the booked loan.
 *
 * Offerable tenors by mode:
 *   - per_tenor → exactly the tenors in the ladder.
 *   - fixed / per_period → every tenor in the country loan policy range.
 *
 * This is the place to eyeball "does my pricing look right?" — it surfaces the
 * declining per-day rate / rising total shape across tenors at a glance.
 */
import { useQuery } from "@tanstack/react-query";
import type { ModelPrice, ModelPriceQuote } from "@dfp/api-client";
import { api } from "../lib/api.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";
import { formatMoney } from "../lib/utils.js";

interface Props {
  readonly price: ModelPrice;
  readonly onClose: () => void;
}

export function ModelPricePlansDialog({ price, onClose }: Props): JSX.Element {
  const cfg = useCountryConfig(true);

  // Which tenors can this model be financed over?
  const tenors: readonly number[] =
    price.lendingPriceMode === "per_tenor"
      ? [...price.perTenor].map((t) => t.tenorMonths).sort((a, b) => a - b)
      : cfg.data
        ? rangeInclusive(cfg.data.loanPolicy.minTenorMonths, cfg.data.loanPolicy.maxTenorMonths)
        : [];

  const quotes = useQuery({
    queryKey: ["model-price-quote", price.id, tenors],
    enabled: tenors.length > 0,
    queryFn: () =>
      Promise.all(
        tenors.map((t) =>
          api.get<ModelPriceQuote>(`/api/model-prices/${price.id}/quote?tenor=${t}`),
        ),
      ),
  });

  const cur = price.valueCurrency;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-simuflex-line px-5 py-3">
          <div>
            <h2 className="font-bold text-simuflex-navy">
              Plans · {price.brand} {price.model}
            </h2>
            <p className="text-[11px] text-simuflex-slate">
              Cost {formatMoney(price.valueAmountMinor, cur)} · Down payment{" "}
              {formatMoney(price.downPaymentMinor, cur)} · figures from the live quote endpoint.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate hover:text-simuflex-navy"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-4">
          {tenors.length === 0 ? (
            <p className="text-sm text-simuflex-slate">
              {price.lendingPriceMode === "per_tenor"
                ? "No tenors in the ladder yet — add some via Edit."
                : "Loading tenor range…"}
            </p>
          ) : quotes.isLoading ? (
            <p className="text-sm text-simuflex-slate">Loading plans…</p>
          ) : quotes.isError ? (
            <p className="text-sm text-red-700">Failed to load plans. {quoteError(quotes.error)}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(quotes.data ?? []).map((q) => (
                <PlanCard key={q.tenorMonths} quote={q} currency={cur} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({
  quote,
  currency,
}: {
  readonly quote: ModelPriceQuote;
  readonly currency: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-simuflex-line bg-slate-50 p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate">
        {quote.tenorMonths} months
      </p>
      <p className="mt-1 text-lg font-bold text-simuflex-navy">
        {formatMoney(quote.perDayMinor, currency)}
        <span className="text-xs font-medium text-simuflex-slate"> /day</span>
      </p>
      <dl className="mt-2 space-y-1 text-xs text-simuflex-slate">
        <Row label="Per week" value={formatMoney(quote.perWeekMinor, currency)} />
        <Row label="Per month" value={formatMoney(quote.perMonthMinor, currency)} />
        <Row label="Total" value={formatMoney(quote.lendingTotalMinor, currency)} strong />
        <Row label="Interest" value={formatMoney(quote.interestTotalMinor, currency)} />
      </dl>
    </div>
  );
}

function Row({
  label,
  value,
  strong = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly strong?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <dt>{label}</dt>
      <dd className={strong ? "font-bold text-simuflex-navy" : "text-simuflex-navy"}>{value}</dd>
    </div>
  );
}

/** Inclusive integer range [from, to]; empty when from > to. */
function rangeInclusive(from: number, to: number): number[] {
  if (!Number.isInteger(from) || !Number.isInteger(to) || from > to) return [];
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

function quoteError(err: unknown): string {
  return err instanceof Error ? err.message : "";
}
